import { useCallback, useEffect, useRef } from 'react';
import Map from 'ol/Map';
import { transform } from 'ol/proj';
import { useOpenLayersContext } from '@ais-ui/react-openlayers';
import { isValidCoordinate } from '@ais-ui/utils';
import {
  getTargetInfoAtPixel,
  useSession,
  ExtractedTargetInfo,
} from '@ais-ui/websockets';
import { Port } from '../AISMapContext';
import {
  useMapContextMenu,
  PortFeature,
  PortAdminFeature,
  ShapeFeature,
  CoordinateFeature,
  VesselFeature,
} from './MapContextMenuContext';
import { GeoJson } from '@ais-ui/api-spec/shapes';
import { useViewMode, ViewMode } from '../../../components/ViewModeProvider';

interface ListenForContextMenuProps {
  map: Map;
}

/** Extract MMSI and name from the backend queryPoint response. */
function extractVesselFromTargetInfo(
  info: ExtractedTargetInfo,
): { mmsi: string; name?: string } | null {
  const target = info.extractedTargetInfo[0];
  if (!target) return null;

  const mmsi =
    'mmsi' in target && target.mmsi ? String(target.mmsi) : undefined;
  if (!mmsi) return null;

  const name =
    'name' in target && target.name ? String(target.name) : undefined;
  return { mmsi, name };
}

export const ListenForContextMenu = ({ map }: ListenForContextMenuProps) => {
  const { openContextMenu } = useMapContextMenu();
  const { displayProjection, dataProjection } = useOpenLayersContext();
  const { olSession, replaySession } = useSession();
  const { viewMode } = useViewMode();

  const session =
    viewMode === ViewMode.Replay && replaySession ? replaySession : olSession;

  // Track if a layer-contextmenu event was handled to prevent duplicate handling
  const layerContextMenuHandledRef = useRef(false);

  const getCoordinateFromEvent = useCallback(
    (event: MouseEvent) => {
      const coordinate = map.getEventCoordinate(event);
      if (!coordinate) return null;

      const [longitude, latitude] = transform(
        coordinate,
        displayProjection,
        dataProjection,
      );

      if (!isValidCoordinate(longitude, latitude)) return null;
      return { longitude, latitude };
    },
    [map, displayProjection, dataProjection],
  );

  useEffect(() => {
    const handleLayerContextMenu = (event: Event) => {
      // Mark that we're handling a layer context menu
      layerContextMenuHandledRef.current = true;

      const customEvent = event as CustomEvent;
      const { properties, geometry, position } = customEvent.detail;

      if (properties?.type === 'port') {
        const port: Port = {
          id: properties.id,
          name: properties.name,
          country: properties.country,
          locode: properties.locode,
          geometry: properties.geometry,
        };

        const portFeature: PortFeature = {
          type: 'port',
          data: port,
        };

        openContextMenu(position, portFeature);
      } else if (
        properties?.type === 'port-outer' ||
        properties?.type === 'port-inner'
      ) {
        // Port admin shapes (boundary / terminals) shown via the Port Admin panel.
        // Both edit the same parent port, identified by portId.
        if (properties.portId == null) return;

        const portAdminFeature: PortAdminFeature = {
          type: 'port-admin',
          data: {
            portId: Number(properties.portId),
            portName:
              properties.type === 'port-outer'
                ? properties.name
                : properties.portName || properties.name || 'Port',
            locode: properties.locode,
            country: properties.country,
          },
        };

        openContextMenu(position, portAdminFeature);
      } else if (properties?.type === 'shape') {
        // Reconstruct GeoJson from feature properties and geometry
        // The geometry is now provided separately from the feature
        const geoJson: GeoJson = {
          type: 'Feature',
          properties: {
            id: properties.id,
            name: properties.name,
            description: properties.description,
            owner: properties.owner,
            radius: properties.radius,
            color: properties.color,
            transparency: properties.transparency,
            parentId: properties.parentId,
            acl: properties.acl,
            area: properties.area,
          },
          geometry: geometry,
        };

        const shapeFeature: ShapeFeature = {
          type: 'shape',
          data: geoJson,
        };

        openContextMenu(position, shapeFeature);
      }
    };

    const handleMapContextMenu = (event: MouseEvent) => {
      // Reset the flag at the start of each context menu event
      // Use a small timeout to allow layer-contextmenu to fire first
      setTimeout(async () => {
        if (layerContextMenuHandledRef.current) {
          // Reset the flag for next time
          layerContextMenuHandledRef.current = false;
          return;
        }

        // Prevent browser context menu
        event.preventDefault();

        const coord = getCoordinateFromEvent(event);
        if (!coord) return;

        const clientPosition = { x: event.clientX, y: event.clientY };

        // Check for vessel at this pixel via the backend session
        try {
          const pixel = map.getEventPixel(event);
          const targetInfo = await getTargetInfoAtPixel(
            session,
            Math.round(pixel[0]),
            Math.round(pixel[1]),
            false, // don't select the target
            false, // don't deselect others
          );

          if (targetInfo) {
            const vessel = extractVesselFromTargetInfo(targetInfo);
            if (vessel) {
              const vesselFeature: VesselFeature = {
                type: 'vessel',
                data: {
                  mmsi: vessel.mmsi,
                  name: vessel.name,
                  longitude: coord.longitude,
                  latitude: coord.latitude,
                },
              };
              openContextMenu(clientPosition, vesselFeature);
              return;
            }
          }
        } catch {
          // If vessel detection fails, fall through to coordinate menu
        }

        // Fallback: coordinate context menu
        const coordinateFeature: CoordinateFeature = {
          type: 'coordinate',
          data: {
            longitude: coord.longitude,
            latitude: coord.latitude,
          },
        };

        openContextMenu(clientPosition, coordinateFeature);
      }, 0);
    };

    const preventDefaultContextMenu = (event: MouseEvent) => {
      // Always prevent default to avoid browser menu showing
      event.preventDefault();
    };

    const mapElement = map.getTargetElement();

    if (mapElement) {
      mapElement.addEventListener('layer-contextmenu', handleLayerContextMenu);
      mapElement.addEventListener('contextmenu', preventDefaultContextMenu);
      mapElement.addEventListener('contextmenu', handleMapContextMenu);
    }

    return () => {
      if (mapElement) {
        mapElement.removeEventListener(
          'layer-contextmenu',
          handleLayerContextMenu,
        );
        mapElement.removeEventListener(
          'contextmenu',
          preventDefaultContextMenu,
        );
        mapElement.removeEventListener('contextmenu', handleMapContextMenu);
      }
    };
  }, [
    map,
    openContextMenu,
    displayProjection,
    dataProjection,
    session,
    getCoordinateFromEvent,
  ]);

  return null;
};
